import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../../config/apiBase";
import { getEnrollmentStatusLabel, normalizeEnrollmentStatus } from "../../utils/enrollmentStatus";
import { getAuthUserHeader } from "../../utils/userRoles";
import AdminShell from "./AdminShell";
import "../../tutor_v2/TutorDashboardV2.css";

const API = API_BASE_URL;

const FILTERS = [
  ["all", "All"],
  ["pending_review", "Pending Review"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
  ["awaiting payment", "Awaiting Payment"],
  ["paid", "Paid"],
  ["activated", "Activated"],
];

function titleize(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();
}

function badgeStyle(kind) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };
  const palette = {
    green: { background: "rgba(76, 175, 80, 0.15)", color: "#9ee6a2", borderColor: "rgba(76, 175, 80, 0.35)" },
    amber: { background: "rgba(255, 193, 7, 0.12)", color: "#ffd977", borderColor: "rgba(255, 193, 7, 0.35)" },
    red: { background: "rgba(244, 67, 54, 0.12)", color: "#ffb1aa", borderColor: "rgba(244, 67, 54, 0.35)" },
    slate: { background: "rgba(148, 163, 184, 0.12)", color: "#d5d9e2", borderColor: "rgba(148, 163, 184, 0.25)" },
  };
  return { ...base, ...(palette[kind] || palette.slate) };
}

function statusTone(kind, value) {
  const token = String(value || "").toUpperCase();
  if (kind === "application") {
    if (token === "APPROVED") return "green";
    if (token === "REJECTED") return "red";
    return "amber";
  }
  if (kind === "payment") {
    if (token === "PAID") return "green";
    if (token === "PAYMENT_SUBMITTED") return "amber";
    return "slate";
  }
  if (kind === "account") {
    return token === "ACTIVATED" ? "green" : "slate";
  }
  return "slate";
}

function rowMatchesFilter(item, filterBy) {
  const token = String(filterBy || "all").toLowerCase();
  const app = normalizeEnrollmentStatus(item?.application_status);
  const pay = String(item?.payment_status || "").toUpperCase();
  const account = String(item?.account_status || "").toUpperCase();
  if (token === "all") return true;
  if (token === "pending" || token === "pending_review") return app === "PENDING_REVIEW";
  if (token === "approved") return app === "APPROVED";
  if (token === "rejected") return app === "REJECTED";
  if (token === "awaiting payment") return app === "APPROVED" && pay !== "PAID";
  if (token === "paid") return pay === "PAID";
  if (token === "activated") return account === "ACTIVATED";
  return true;
}

export default function AdminEnrollmentsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [pendingReviewItems, setPendingReviewItems] = useState([]);
  const mountedRef = useRef(false);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestSeq.current;
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (filterBy && filterBy !== "all") params.set("filter_by", filterBy);
      const url = `${API}/api/admin/enrollments${params.toString() ? `?${params.toString()}` : ""}`;
      const pendingReviewUrl = `${API}/api/admin/enrollments?filter_by=pending_review`;
      const [res, pendingRes] = await Promise.all([
        fetch(url, { cache: "no-store", headers: { "X-User": getAuthUserHeader() } }),
        fetch(pendingReviewUrl, { cache: "no-store", headers: { "X-User": getAuthUserHeader() } }),
      ]);
      const data = await res.json().catch(() => null);
      const pendingData = await pendingRes.json().catch(() => null);
      if (!res.ok) {
        throw new Error((data && data.detail) || "Failed to load enrollments");
      }
      if (!pendingRes.ok) {
        throw new Error((pendingData && pendingData.detail) || "Failed to load pending review count");
      }
      if (!mountedRef.current || requestId !== requestSeq.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setPendingReviewItems(Array.isArray(pendingData?.items) ? pendingData.items : []);
    } catch (err) {
      if (!mountedRef.current || requestId !== requestSeq.current) return;
      setError(err?.message || "Failed to load enrollments");
      setItems([]);
      setPendingReviewItems([]);
    } finally {
      if (mountedRef.current && requestId === requestSeq.current) setLoading(false);
    }
  }, [filterBy, search]);

  const deleteEnrollment = async (item) => {
    if (!window.confirm(`Delete enrollment request for ${item.full_name || item.email || "this applicant"}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/enrollments/${encodeURIComponent(item.id)}`, {
        method: "DELETE",
        headers: { "X-User": getAuthUserHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to delete enrollment");
      await load();
    } catch (err) {
      window.alert(err?.message || "Failed to delete enrollment");
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const timer = window.setTimeout(() => {
      load();
    }, 180);
    return () => {
      mountedRef.current = false;
      window.clearTimeout(timer);
    };
  }, [load]);

  const summary = useMemo(() => {
    const approved = items.filter((item) => normalizeEnrollmentStatus(item.application_status) === "APPROVED");
    return {
      newRequests: pendingReviewItems.filter((item) => normalizeEnrollmentStatus(item.application_status) === "PENDING_REVIEW").length,
      awaitingPayment: approved.filter((item) => String(item.payment_status || "").toUpperCase() !== "PAID").length,
      readyToActivate: items.filter(
        (item) =>
          normalizeEnrollmentStatus(item.application_status) === "APPROVED" &&
          String(item.payment_status || "").toUpperCase() === "PAID" &&
          String(item.account_status || "").toUpperCase() !== "ACTIVATED"
      ).length,
    };
  }, [items, pendingReviewItems]);

  const firstPendingReviewId = pendingReviewItems[0]?.id;

  return (
    <AdminShell title="Enrollment Requests" subtitle="Review applications, payments, and student activation.">

      <section
        className="td-card reveal"
        style={{
          "--delay": ".08s",
          padding: 16,
          display: "grid",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
          {[
            ["New Enrolment Requests", summary.newRequests],
            ["Awaiting payment", summary.awaitingPayment],
            ["Ready to activate", summary.readyToActivate],
          ].map(([label, value]) => (
            <button
              key={label}
              type="button"
              disabled={label === "New Enrolment Requests" && !firstPendingReviewId}
              onClick={() => {
                const map = {
                  "New Enrolment Requests": firstPendingReviewId ? `/admin/enrollments/${firstPendingReviewId}` : "/admin/enrollments",
                  "Awaiting payment": "awaiting payment",
                  "Ready to activate": "activated",
                };
                const target = map[label] || "all";
                if (typeof target === "string" && target.startsWith("/admin/enrollments/")) {
                  navigate(target);
                  return;
                }
                setFilterBy(target);
              }}
              style={{
                textAlign: "left",
                padding: 14,
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,.1)",
                background: "rgba(255,255,255,.03)",
                color: "inherit",
                cursor: "pointer",
                opacity: label === "New Enrolment Requests" && !firstPendingReviewId ? 0.65 : 1,
              }}
            >
              <div style={{ fontSize: 12, color: "var(--muted, #b8b3aa)", marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{value}</div>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name or email"
            aria-label="Search enrollment requests"
            style={{
              flex: "1 1 240px",
              minWidth: 220,
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,.12)",
              background: "rgba(8,10,14,.65)",
              color: "#e7e3dc",
            }}
          />
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilterBy(value)}
              style={{
                padding: "9px 12px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,.12)",
                background: filterBy === value ? "rgba(201,165,106,.18)" : "rgba(255,255,255,.03)",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => load()}
            style={{
              padding: "9px 12px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,.12)",
              background: "rgba(255,255,255,.03)",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>
      </section>

      {loading ? <div className="td-loading">Loading enrollment requests…</div> : null}
      {error ? <div className="td-empty" style={{ color: "#ffb1aa" }}>{error}</div> : null}

      {!loading && !error && items.length === 0 ? (
        <div className="td-empty">No enrollment requests found.</div>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <section className="td-card reveal" style={{ "--delay": ".1s", padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1120 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted, #b8b3aa)" }}>
                {["Applicant", "Email", "Phone", "Programme", "Coupon", "Request date", "Application", "Payment", "Account", ""].map((label) => (
                  <th key={label} style={{ padding: 14, borderBottom: "1px solid rgba(255,255,255,.08)", fontSize: 12, letterSpacing: 0.6, textTransform: "uppercase" }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.filter((item) => rowMatchesFilter(item, filterBy)).map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/admin/enrollments/${item.id}`)}
                  style={{ borderBottom: "1px solid rgba(255,255,255,.06)", cursor: "pointer" }}
                >
                  <td style={{ padding: 14 }}>
                    <div style={{ fontWeight: 700 }}>{item.full_name || "Unnamed"}</div>
                  </td>
                  <td style={{ padding: 14 }}>{item.email || ""}</td>
                  <td style={{ padding: 14 }}>{item.phone || ""}</td>
                  <td style={{ padding: 14 }}>{item.programme || ""}</td>
                  <td style={{ padding: 14 }}>{item.coupon_code || "—"}</td>
                  <td style={{ padding: 14 }}>{item.submitted_date ? new Date(item.submitted_date).toLocaleString() : ""}</td>
                  <td style={{ padding: 14 }}>
                    <span style={badgeStyle(statusTone("application", item.application_status))}>
                      {getEnrollmentStatusLabel(item.application_status)}
                    </span>
                  </td>
                  <td style={{ padding: 14 }}>
                    <span style={badgeStyle(statusTone("payment", item.payment_status))}>
                      {titleize(item.payment_status)}
                    </span>
                  </td>
                  <td style={{ padding: 14 }}>
                    <span style={badgeStyle(statusTone("account", item.account_status))}>
                      {titleize(item.account_status)}
                    </span>
                  </td>
                  <td style={{ padding: 14, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/admin/enrollments/${item.id}`);
                      }}
                      style={{
                        padding: "9px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,.12)",
                        background: "rgba(201,165,106,.16)",
                        color: "#fff",
                        cursor: "pointer",
                        marginRight: 8,
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteEnrollment(item);
                      }}
                      disabled={normalizeEnrollmentStatus(item.application_status) === "APPROVED"}
                      style={{
                        padding: "9px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,.12)",
                        background: "rgba(244, 67, 54, .16)",
                        color: "#fff",
                        cursor: "pointer",
                        opacity: normalizeEnrollmentStatus(item.application_status) === "APPROVED" ? 0.45 : 1,
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </AdminShell>
  );
}
